import { RefreshCw, Download } from 'lucide-react'
// Steve Jobs: Removed Flame and LayoutGrid - navigation via sidebar is enough
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import HealthScore from '@/components/pulse/HealthScore'
import EmergencesFeed from '@/components/pulse/EmergencesFeed'
import RecentIngestions from '@/components/pulse/RecentIngestions'
import HotConcepts from '@/components/pulse/HotConcepts'
import BlindSpots from '@/components/pulse/BlindSpots'
import ActivitySparkline from '@/components/pulse/ActivitySparkline'
import { usePulseContext } from '@/contexts/PulseContext'

export default function PulseView() {
  const { data: pulseData, loading, error, refetch } = usePulseContext()
  // Steve Jobs: Removed navigate - navigation via sidebar is sufficient

  if (loading) {
    return (
      <div className="w-full h-[80vh] flex flex-col items-center justify-center space-y-8" aria-live="polite" aria-busy="true">
        <Skeleton className="w-16 h-16 rounded-full" />
        <Skeleton className="h-4 w-64" />
        <p className="text-rsb-text-secondary font-mono text-sm tracking-widest uppercase">Initializing Neural Link...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full py-20 flex justify-center">
        <Card className="glass-panel p-8 rounded-2xl text-center max-w-md border-red-500/20">
          <CardContent className="p-0">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-display font-bold text-white mb-2">System Error</h2>
            <p className="text-rsb-text-secondary text-sm mb-6">{error.message}</p>
            <Button onClick={refetch} className="w-full">Reconnect</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const hasEmergences = pulseData?.emergences && pulseData.emergences.length > 0
  const hasConcepts = pulseData?.top_concepts && pulseData.top_concepts.length > 0
  const hasIngestions = pulseData?.recent_ingestions && pulseData.recent_ingestions.length > 0

  return (
    <div className="w-full space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-white tracking-tight">
            PULSE
          </h1>
          <div className="flex items-center gap-2 text-xs text-rsb-text-secondary font-mono mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>ONLINE</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={refetch}
          className="glass-panel hover:bg-white/10 rounded-xl text-rsb-text-secondary hover:text-white"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Health Score - Main Focus */}
      {pulseData && (
        <Card className="glass-panel rounded-2xl overflow-hidden border-white/5">
          <HealthScore healthScore={pulseData.stats.health_score} />
        </Card>
      )}

      {/* Two Column Layout: Ingestions + Hot Concepts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Ingestions */}
        <Card className="glass-panel rounded-2xl p-6 border-white/5">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <Download className="w-4 h-4 text-rsb-accent" />
            Últimas Ingestões
          </h3>
          {hasIngestions ? (
            <RecentIngestions ingestions={pulseData!.recent_ingestions} />
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-white/40">Nenhuma ingestão ainda</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('intent-bar:focus', { detail: { prefill: 'https://' } }))
                }}
                className="mt-2 text-xs text-rsb-accent"
              >
                Adicionar conteúdo
              </Button>
            </div>
          )}
        </Card>

        {/* Hot Concepts */}
        <Card className="glass-panel rounded-2xl p-6 border-white/5">
          {hasConcepts ? (
            <HotConcepts concepts={pulseData!.top_concepts} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center py-8">
              <p className="text-sm text-white/40">Nenhum conceito ainda</p>
              <p className="text-xs text-white/30 mt-1">Ingira conteúdo para criar conceitos</p>
            </div>
          )}
        </Card>
      </div>

      {/* Emergences - Conditional */}
      {hasEmergences && (
        <Card className="glass-panel rounded-2xl p-1 border-rsb-accent/20">
          <EmergencesFeed emergences={pulseData!.emergences} />
        </Card>
      )}

      {/* Blind Spots - Conditional (component handles empty state internally) */}
      <BlindSpots />

      {/* Activity Sparkline */}
      <Card className="glass-panel rounded-2xl p-4 border-white/5">
        <ActivitySparkline days={30} />
      </Card>

      {/* Steve Jobs: Single primary action - the most important thing */}
      <button
        onClick={() => {
          window.dispatchEvent(new CustomEvent('intent-bar:focus', { detail: { prefill: 'https://' } }))
        }}
        className="w-full px-6 py-4 rounded-2xl bg-rsb-accent/10 hover:bg-rsb-accent/20 border border-rsb-accent/20 transition-all flex items-center justify-center gap-3 group"
      >
        <Download className="w-5 h-5 text-rsb-accent group-hover:text-white transition-colors" />
        <span className="text-sm font-medium text-white">Adicionar Conhecimento</span>
      </button>
    </div>
  )
}
